// CSRFトークン切れ(ApplicationController#handle_invalid_authenticity_token)の場合だけ、
// ページをリロードしてトークンを取り直す。通常の401・403ではリロードしない。
const CSRF_ERROR_CODE = 'invalid_authenticity_token';

// 自動リロードが続く(Cookieがブロックされている、CSRF検証をスキップするAPIの成功で解除される等)と
// 無限リロードになるため、同じページでの自動リロードは連続1回までに制限する
const AUTO_RELOAD_STORAGE_KEY = 'wrapFetch:autoReloadedPage';

// 複数のリクエストが同時にエラーを返した場合に、reloadを重複させないためのフラグ
let reloadRequested = false;

const currentPage = () => `${window.location.pathname}${window.location.search}`;

const readAutoReloadedPage = () => {
  try {
    return window.sessionStorage.getItem(AUTO_RELOAD_STORAGE_KEY);
  } catch {
    return null;
  }
};

// 自動リロードで表示されたページでは、記録が解除されても同じページのうちは再リロードしない
const loadedByAutoReload = readAutoReloadedPage() === currentPage();

// リロードした場合は true を返す
// (sessionStorageが使えない場合はループを防げないため、リロードしない)
const reloadOnce = () => {
  if (reloadRequested) return true;
  if (loadedByAutoReload) return false;

  try {
    if (window.sessionStorage.getItem(AUTO_RELOAD_STORAGE_KEY) === currentPage()) return false;
    window.sessionStorage.setItem(AUTO_RELOAD_STORAGE_KEY, currentPage());
  } catch {
    return false;
  }

  reloadRequested = true;
  window.location.reload();
  return true;
};

// 更新系リクエストが成功したら、次に表示するページでは再び自動リロードできるよう記録を解除する
export const clearAutoReloadRecord = () => {
  try {
    window.sessionStorage.removeItem(AUTO_RELOAD_STORAGE_KEY);
  } catch {
    // sessionStorageが使えない場合は記録も残っていない
  }
};

const isCsrfError = async (response: Response) => {
  if (response.status !== 403) return false;
  try {
    const body = (await response.clone().json()) as { error?: unknown };
    return body?.error === CSRF_ERROR_CODE;
  } catch {
    return false;
  }
};

// CSRFエラーならリロードを試み、リロードした場合は true を返す
export const reloadIfCsrfError = async (response: Response) => {
  if (!(await isCsrfError(response))) return false;
  return reloadOnce();
};

const parseResponse = async <T>(url: RequestInfo, options: RequestInit | undefined, response: Response) => {
  if (await reloadIfCsrfError(response)) return new Promise<T>(() => {});

  const method = (options?.method ?? (typeof url === 'string' ? 'GET' : url.method)).toUpperCase();
  if (response.ok && method !== 'GET') clearAutoReloadRecord();

  const data = (await response.json()) as T;
  return data;
};

export const wrapFetch = async <T>(url: RequestInfo, options?: RequestInit) => {
  const response = await fetch(url, options);
  return parseResponse<T>(url, options, response);
};

// ログイン必須ページ専用。401(未ログイン=セッション切れ)の場合はリロードし、
// 企業・求職者それぞれのログイン画面への遷移はRails側の認証フィルタに任せる。
// 権限不足を401で返すAPIに使うと誤ってリロードするため、未ログイン時のみ401を返すAPIに限って使うこと。
export const wrapFetchRequiringLogin = async <T>(url: RequestInfo, options?: RequestInit) => {
  const response = await fetch(url, options);
  if (response.status === 401 && reloadOnce()) return new Promise<T>(() => {});
  return parseResponse<T>(url, options, response);
};
